import { storageService } from './storageService';

const assetTypeForFile = (file) => {
  if (!file) return 'Asset';
  if (file.type.includes('pdf')) return 'Document';
  if (file.type.includes('image')) return 'Image';
  if (file.type.includes('video')) return 'Video';
  return 'File';
};

const createAssetId = () => `asset-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const quickFormPresets = [
  {
    id: 'meeting',
    label: 'Meeting form',
    title: 'Meeting form',
    description: 'Capture the agenda, preparation notes, minutes, decisions, and follow-ups for a meeting.',
    submitLabel: 'Submit meeting notes',
    fields: [
      { id: 'meeting-title', label: 'Meeting title', type: 'text', required: true, placeholder: 'Weekly leadership sync' },
      { id: 'meeting-date', label: 'Meeting date', type: 'date', required: true, placeholder: '' },
      { id: 'attendees', label: 'Attendees', type: 'textarea', required: false, placeholder: 'Names, teams, or email addresses' },
      { id: 'agenda', label: 'Agenda', type: 'textarea', required: true, placeholder: 'Topics to discuss' },
      { id: 'meeting-prep', label: 'Meeting prep', type: 'textarea', required: false, placeholder: 'Context, links, reading, or questions to prepare' },
      { id: 'minutes', label: 'Minutes', type: 'textarea', required: false, placeholder: 'Notes captured during or after the meeting' },
      { id: 'decisions', label: 'Decisions', type: 'textarea', required: false, placeholder: 'Decisions made and rationale' },
      { id: 'action-items', label: 'Action items', type: 'textarea', required: false, placeholder: 'Owner, task, and due date' },
    ],
  },
  {
    id: 'blank',
    label: 'Blank form',
    title: 'Quick form',
    description: 'Collect structured responses from this shared link.',
    submitLabel: 'Submit response',
    fields: [
      { id: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Your name' },
      { id: 'notes', label: 'Notes', type: 'textarea', required: false, placeholder: 'Add details' },
    ],
  },
];

const createQuickFormFromPreset = (presetId = 'meeting') => {
  const preset = quickFormPresets.find((item) => item.id === presetId) || quickFormPresets[0];

  return {
    enabled: true,
    preset: `quick-${preset.id}`,
    title: preset.title,
    description: preset.description,
    buttonLabel: preset.title,
    submitLabel: preset.submitLabel,
    fields: preset.fields.map((field) => ({ ...field })),
  };
};

export const getYouTubeVideoId = (value = '') => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';

  try {
    const url = new URL(/^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`);
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();

    if (host === 'youtu.be') {
      return url.pathname.split('/').filter(Boolean)[0] || '';
    }

    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      if (url.pathname === '/watch') return url.searchParams.get('v') || '';
      if (url.pathname.startsWith('/shorts/') || url.pathname.startsWith('/embed/')) {
        return url.pathname.split('/').filter(Boolean)[1] || '';
      }
    }
  } catch (error) {
    return '';
  }

  return '';
};

export const buildYouTubeEmbedUrl = (videoId = '') => {
  if (!videoId) return '';
  const params = new URLSearchParams({
    controls: '1',
    mute: '1',
    playsinline: '1',
    rel: '0',
  });
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
};

export const parseShareListItems = (value = '') => String(value || '')
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean)
  .map((line) => {
    const [title, detail, tags, actionLabel, actionUrl, imageUrl] = line.split('|').map((part) => part.trim());

    return {
      id: `list-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: title || 'Untitled item',
      detail: detail || '',
      tags: String(tags || '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      actionLabel: actionLabel || '',
      actionUrl: actionUrl || '',
      imageUrl: imageUrl || '',
    };
  });

export const shareListItemsToText = (items = []) => (Array.isArray(items) ? items : [])
  .map((item) => [
    item.title,
    item.detail,
    Array.isArray(item.tags) ? item.tags.join(', ') : item.tags,
    item.actionLabel,
    item.actionUrl,
    item.imageUrl,
  ].map((part) => String(part || '').trim()).join(' | ').replace(/(?:\s\|\s)*$/g, ''))
  .join('\n');

export const assetsService = {
  createHtmlEmbed({ title, embedHtml }) {
    return {
      id: `asset-html-${Date.now()}`,
      title: title?.trim() || 'HTML embed',
      description: '',
      price: '',
      reducedPrice: '',
      discountType: 'percentage',
      discountValue: '',
      currency: 'ZAR',
      pricePrefix: '',
      quantity: '',
      source: '',
      flag: '',
      prepay: '',
      active: true,
      type: 'HTML Embed',
      size: 'Embed',
      audience: 'Manual embed',
      status: 'Draft',
      embedHtml,
      color: 'from-slate-100 to-slate-300',
      shape: 'embed',
    };
  },

  createLinkInBio({ title, description, linkItems }) {
    return {
      id: `asset-link-bio-${Date.now()}`,
      title: title?.trim() || 'Link in bio',
      description: description || '',
      price: '',
      reducedPrice: '',
      discountType: 'percentage',
      discountValue: '',
      currency: 'ZAR',
      pricePrefix: '',
      quantity: '',
      source: '',
      flag: '',
      prepay: '',
      active: true,
      type: 'Link In Bio',
      size: `${linkItems.length} links`,
      audience: 'Manual links',
      status: 'Draft',
      linkItems,
      color: 'from-slate-100 to-slate-300',
      shape: 'link-bio',
    };
  },

  createProfileCard({
    title,
    description,
    tagline,
    qualifications,
    experience,
    tags,
    externalUrl,
    ctaLabel,
    section,
    iconUrl,
    iconFile,
    isPrimaryAction = false,
  }) {
    return {
      id: `asset-profile-card-${Date.now()}`,
      title: title?.trim() || 'Untitled profile',
      description: description || '',
      tagline: tagline?.trim() || '',
      qualifications: Array.isArray(qualifications) ? qualifications : [],
      experience: experience || '',
      tags: Array.isArray(tags) ? tags : [],
      price: '',
      reducedPrice: '',
      discountType: 'percentage',
      discountValue: '',
      currency: 'ZAR',
      pricePrefix: '',
      quantity: '',
      source: '',
      flag: '',
      prepay: '',
      active: true,
      type: 'Profile Card',
      size: 'Profile',
      audience: section?.trim() || 'Personal',
      status: 'Draft',
      externalUrl: externalUrl?.trim() || '',
      ctaLabel: ctaLabel?.trim() || 'Open profile',
      section: section?.trim() || 'Personal',
      iconUrl: iconUrl || '',
      iconFile: iconFile || null,
      isPrimaryAction: Boolean(isPrimaryAction),
      color: isPrimaryAction ? 'from-slate-950 to-slate-800' : 'from-slate-100 to-slate-300',
      shape: 'link-bio',
    };
  },

  createLinkInBioUrl(profileCard) {
    return this.createProfileCard(profileCard);
  },

  createMarkdownArticle({ title, articleMarkdown }) {
    return {
      id: `asset-markdown-${Date.now()}`,
      title: title?.trim() || 'Rich content',
      description: '',
      price: '',
      reducedPrice: '',
      discountType: 'percentage',
      discountValue: '',
      currency: 'ZAR',
      pricePrefix: '',
      quantity: '',
      source: '',
      flag: '',
      prepay: '',
      active: true,
      type: 'Rich Content',
      size: 'Content',
      audience: 'Manual content',
      status: 'Draft',
      articleMarkdown,
      color: 'from-slate-100 to-slate-300',
      shape: 'article',
    };
  },

  createYouTubeVideo({ title, youtubeUrl }) {
    const youtubeVideoId = getYouTubeVideoId(youtubeUrl);

    return {
      id: `asset-youtube-${Date.now()}`,
      title: title?.trim() || 'YouTube video',
      description: '',
      price: '',
      reducedPrice: '',
      discountType: 'percentage',
      discountValue: '',
      currency: 'ZAR',
      pricePrefix: '',
      quantity: '',
      source: '',
      flag: '',
      prepay: '',
      active: true,
      type: 'YouTube Video',
      size: 'YouTube',
      audience: 'Manual video',
      status: 'Draft',
      youtubeUrl: youtubeUrl?.trim() || '',
      youtubeVideoId,
      youtubeEmbedUrl: buildYouTubeEmbedUrl(youtubeVideoId),
      color: 'from-slate-950 to-slate-800',
      shape: 'youtube',
    };
  },

  createForm({ title, description, presetId = 'meeting' }) {
    const form = createQuickFormFromPreset(presetId);
    const resolvedTitle = title?.trim() || form.title;

    return {
      id: `asset-form-${Date.now()}`,
      title: resolvedTitle,
      description: description || form.description,
      price: '',
      reducedPrice: '',
      discountType: 'percentage',
      discountValue: '',
      currency: 'ZAR',
      pricePrefix: '',
      quantity: '',
      source: '',
      flag: '',
      prepay: '',
      active: true,
      type: 'Form',
      size: `${form.fields.length} fields`,
      audience: 'Quick form',
      status: 'Draft',
      form: {
        ...form,
        title: resolvedTitle,
        description: description || form.description,
      },
      color: 'from-white to-slate-100',
      shape: 'form',
    };
  },

  createShareList({
    title,
    description,
    listItems,
    listStyle = 'standard',
    iconUrl = '',
    iconFile = null,
    tags = [],
    actionLabel = '',
    actionUrl = '',
  }) {
    const items = Array.isArray(listItems) ? listItems : parseShareListItems(listItems);

    return {
      id: `asset-list-${Date.now()}`,
      title: title?.trim() || 'Untitled list',
      description: description || '',
      price: '',
      reducedPrice: '',
      discountType: 'percentage',
      discountValue: '',
      currency: 'ZAR',
      pricePrefix: '',
      quantity: '',
      source: '',
      flag: '',
      prepay: '',
      active: true,
      type: 'List',
      size: `${items.length} items`,
      audience: 'Manual list',
      status: 'Draft',
      listItems: items,
      listStyle,
      tags: Array.isArray(tags) ? tags : [],
      actionLabel: actionLabel?.trim() || '',
      actionUrl: actionUrl?.trim() || '',
      iconUrl,
      iconFile,
      color: 'from-white to-slate-100',
      shape: 'list',
    };
  },

  fromFiles(files) {
    return Array.from(files || []).map((file) => ({
      id: createAssetId(),
      title: file.name.replace(/\.[^/.]+$/, ''),
      description: '',
      price: '',
      reducedPrice: '',
      discountType: 'percentage',
      discountValue: '',
      currency: 'ZAR',
      pricePrefix: '',
      quantity: '',
      source: '',
      flag: '',
      prepay: '',
      active: true,
      type: assetTypeForFile(file),
      size: `${Math.max(file.size / 1000000, 0.1).toFixed(1)} MB`,
      audience: 'New upload',
      status: 'Draft',
      preview: file.type.includes('image') || file.type.includes('video') ? URL.createObjectURL(file) : null,
      color: 'from-slate-100 to-slate-300',
      shape: file.type.includes('image') || file.type.includes('video') ? 'standard' : 'document',
      file,
    }));
  },

  async uploadFiles(linkId, files) {
    const localAssets = this.fromFiles(files);
    const uploadedAssets = await Promise.all(localAssets.map(async (asset) => {
      const upload = await storageService.uploadAssetFile(linkId, asset.file);
      const { file, ...assetData } = asset;
      return {
        ...assetData,
        preview: upload.downloadUrl,
        downloadUrl: upload.downloadUrl,
        storagePath: upload.path,
        contentType: upload.contentType,
        uploadBytes: upload.size || asset.file.size || 0,
      };
    }));

    return uploadedAssets;
  },
};
